import type { MaterialLibraryEntry } from './types'

export interface MaterialLibraryProjectGroup {
  key: string
  projectId: number | null
  projectTitle: string
  heading: string
  summary: string
  isCurrentProject: boolean
  entries: MaterialLibraryEntry[]
}

function toTimestamp(value?: string | null) {
  if (!value) {
    return 0
  }
  const timestamp = Date.parse(value)
  return Number.isFinite(timestamp) ? timestamp : 0
}

function resolveProjectTitle(entry: MaterialLibraryEntry, linkedProjectId: number | null) {
  if (linkedProjectId && entry.project_id === linkedProjectId) {
    return entry.project_title && entry.project_title !== '当前项目'
      ? entry.project_title
      : `项目 ${entry.project_id}`
  }
  return entry.project_title || `项目 ${entry.project_id}`
}

export function groupMaterialEntriesByProject(entries: MaterialLibraryEntry[], linkedProjectId: number | null) {
  const groups = new Map<number | null, MaterialLibraryProjectGroup & { latestUpdatedAt: number }>()

  entries.forEach((entry) => {
    const projectId = Number.isFinite(entry.project_id) ? entry.project_id : null
    const existing = groups.get(projectId)
    const latestUpdatedAt = toTimestamp(entry.updated_at)

    if (existing) {
      existing.entries.push(entry)
      existing.latestUpdatedAt = Math.max(existing.latestUpdatedAt, latestUpdatedAt)
      return
    }

    const isCurrentProject = Boolean(linkedProjectId && projectId === linkedProjectId)
    const projectTitle = resolveProjectTitle(entry, linkedProjectId)
    groups.set(projectId, {
      key: projectId === null ? 'project-unassigned' : `project-${projectId}`,
      projectId,
      projectTitle,
      heading: isCurrentProject ? `${projectTitle} · 当前项目` : projectTitle,
      summary: isCurrentProject
        ? `当前项目共 ${1} 条素材`
        : `${projectId ? `项目 ${projectId}` : '未关联项目'}共 ${1} 条素材`,
      isCurrentProject,
      entries: [entry],
      latestUpdatedAt,
    })
  })

  return Array.from(groups.values())
    .map((group) => ({
      key: group.key,
      projectId: group.projectId,
      projectTitle: group.projectTitle,
      heading: group.heading,
      summary: group.isCurrentProject
        ? `当前项目共 ${group.entries.length} 条素材`
        : `${group.projectId ? `项目 ${group.projectId}` : '未关联项目'}共 ${group.entries.length} 条素材`,
      isCurrentProject: group.isCurrentProject,
      entries: group.entries.slice().sort((left, right) => toTimestamp(right.updated_at) - toTimestamp(left.updated_at)),
      latestUpdatedAt: group.latestUpdatedAt,
    }))
    .sort((left, right) => {
      if (left.isCurrentProject !== right.isCurrentProject) {
        return left.isCurrentProject ? -1 : 1
      }
      if (left.latestUpdatedAt !== right.latestUpdatedAt) {
        return right.latestUpdatedAt - left.latestUpdatedAt
      }
      return left.heading.localeCompare(right.heading, 'zh-CN')
    })
    .map(({ latestUpdatedAt, ...group }) => group)
}